import { useState } from 'react';
import { flushSync } from 'react-dom';
import { useAuth } from '@/shared/auth';
import { Button, Card, Eyebrow, FormField, Input, Textarea } from '@/shared/components';
import { I } from '@/shared/icons';
import { buildCheckInUrl } from './buildCheckInUrl';
import { QrPoster } from './QrPoster';

const DEFAULT_TITLE = 'Willkommen';
const DEFAULT_SUBTITLE =
  'Halten Sie Ihre Buchungsnummer und Ihren Nachnamen bereit — der QR-Code führt Sie direkt zum Online-Check-in.';
const PREVIEW_SCALE = 0.34;

/**
 * QR-Poster-Bereich für „Gäste & QR-Poster". Erzeugt on-demand einen druckbaren
 * A4-QR-Poster für den tenantweiten Check-in-Link. Titel und Begleittext sind frei
 * editierbar; nichts wird gespeichert (kein Backend, keine Persistenz). Das Kürzel kommt
 * aus dem Workspace-Kontext des Tokens und bleibt überschreibbar — Live-QR + sichtbare URL
 * erlauben die Kontrolle vor dem Druck.
 */
export function QrPosterSection() {
  const { user } = useAuth();
  const [slug, setSlug] = useState(user?.workspaceSlug ?? '');
  const [title, setTitle] = useState(DEFAULT_TITLE);
  const [subtitle, setSubtitle] = useState(DEFAULT_SUBTITLE);
  const [isPrinting, setIsPrinting] = useState(false);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const trimmedSlug = slug.trim();
  const url = buildCheckInUrl(origin, trimmedSlug);
  const ready = trimmedSlug.length > 0;

  const handlePrint = () => {
    if (!ready) return;
    // Print-Kopie synchron mounten, dann drucken, danach zuverlässig wieder abbauen — so
    // isoliert das @media-print in globals.css ausschließlich das Poster und es blitzt
    // nichts auf. Das finally garantiert den Abbau auch wenn window.print() wirft, sonst
    // bliebe die Print-Kopie hängen und das nächste Ctrl+P würde nur das Poster drucken.
    flushSync(() => setIsPrinting(true));
    try {
      window.print();
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <Card>
      <div className="p-6">
        <Eyebrow variant="muted">Gastzugang</Eyebrow>
        <h2 className="text-ink mt-2 font-serif text-2xl">QR-Poster</h2>
        <p className="text-ink-soft mt-2 max-w-prose text-sm">
          Tenantweiter QR-Code für den Guest-Check-in — zum Aushängen. Titel und Begleittext lassen
          sich frei anpassen. Es wird nichts gespeichert.
        </p>

        <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_auto]">
          <div className="flex flex-col gap-4">
            <FormField
              label="Tenant-Slug"
              hint="Wie bei der Anmeldung (z. B. stayon-hq). Bestimmt, auf welchen Tenant der Check-in zeigt."
            >
              <Input
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="stayon-hq"
                autoCapitalize="none"
                autoComplete="off"
                spellCheck={false}
              />
            </FormField>
            <FormField label="Titel">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={40} />
            </FormField>
            <FormField label="Begleittext">
              <Textarea
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                rows={3}
                maxLength={240}
              />
            </FormField>

            <div className="border-line bg-card-alt rounded-md border px-3 py-2">
              <div className="text-ink-faint text-[11px] font-semibold uppercase tracking-[0.12em]">
                Check-in-Link
              </div>
              <div className="text-ink-soft mt-1 break-all font-mono text-xs">
                {ready ? url : 'Tenant-Slug eingeben, um den Link zu erzeugen.'}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="brand"
                onClick={handlePrint}
                disabled={!ready}
                leftIcon={<I name="Download" size={14} />}
              >
                Als A4 drucken / PDF
              </Button>
              <span className="text-ink-faint text-xs">Nicht gespeichert</span>
            </div>
          </div>

          <div className="justify-self-center lg:justify-self-end">
            <div
              aria-hidden={!ready}
              className="border-line overflow-hidden rounded-md border shadow-sm"
              style={{
                width: `calc(210mm * ${PREVIEW_SCALE})`,
                height: `calc(297mm * ${PREVIEW_SCALE})`,
              }}
            >
              {ready ? (
                <div style={{ transform: `scale(${PREVIEW_SCALE})`, transformOrigin: 'top left' }}>
                  <QrPoster title={title} subtitle={subtitle} url={url} />
                </div>
              ) : (
                <div className="text-ink-faint flex h-full items-center justify-center px-6 text-center text-xs">
                  Vorschau erscheint, sobald ein Tenant-Slug eingegeben ist.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isPrinting && (
        <div className="hidden print:block" data-qr-poster-print>
          {/* @page nur während des Poster-Drucks setzen (nicht global in globals.css),
              damit A4/margin:0 keine anderen Druckvorgänge der App beeinflusst. */}
          <style>{'@page { size: A4; margin: 0; }'}</style>
          <QrPoster title={title} subtitle={subtitle} url={url} />
        </div>
      )}
    </Card>
  );
}
